import React from 'react';
import {
  AutoFocusedForm,
  notEmpty,
  PhotoField,
  TextField,
  DateField,
  hexColor,
  required,
} from 'components';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  createStyles,
  Divider,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { MutableButton } from 'ui/MutableButton';
import { RedButton } from 'components/Buttons';
import { Field } from 'react-final-form';
import { SeasonByIdQuery } from '../../../../schema';
import { useEditSeasonForm } from './useEditSeasonForm';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      minWidth: 500,
    },
    sectionHeader: {
      fontWeight: 500,
      marginBottom: theme.spacing(2),
    },
    accessButton: {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.success.dark,
      '&:hover': {
        color: theme.palette.success.dark,
        backgroundColor: theme.palette.success.light,
      },
    },
    actions: {
      padding: theme.spacing(2),
    },
  })
);

interface EditSeasonFormProps {
  seasonId: string;
  data: SeasonByIdQuery | undefined;
}

export const EditSeasonForm = ({ seasonId, data }: EditSeasonFormProps) => {
  const styles = useStyles();
  const { initialValues, onSubmit, onCancel, loading } = useEditSeasonForm({ seasonId, data });

  return (
    <AutoFocusedForm
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, pristine }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Card className={styles.root}>
              <CardHeader title="Редактирование сезона" />
              <Divider />

              <CardContent>
                <Typography variant="h6" className={styles.sectionHeader}>
                  Основная информация
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Field
                      name="imageId"
                      label="Изображение сезона"
                      component={PhotoField}
                      url={data?.season?.generalInformation.image?.url}
                      validate={required}
                    />
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Field
                          name="title"
                          label="Название"
                          component={TextField}
                          type="text"
                          validate={notEmpty}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field
                          name="start"
                          label="Начало"
                          component={DateField}
                          returnTime={false}
                          fullWidth
                          validate={notEmpty}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field
                          name="end"
                          label="Окончание"
                          component={DateField}
                          returnTime={false}
                          fullWidth
                          validate={notEmpty}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          name="description"
                          label="Описание"
                          component={TextField}
                          type="text"
                          multiline
                          rows={4}
                          validate={notEmpty}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field
                          name="brandColor"
                          label="Брендированный цвет"
                          component={TextField}
                          type="text"
                          placeholder="#RRGGBB"
                          validate={hexColor}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>

              <Divider />

              <CardContent>
                <Typography variant="h6" className={styles.sectionHeader}>
                  Информация о валюте сезона
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Field
                      name="currencyIconId"
                      label="Иконка валюты"
                      component={PhotoField}
                      url={data?.season?.currency.icon?.url}
                      validate={required}
                    />
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Field
                          name="nominativeSingular"
                          label="Им. падеж ед. число"
                          component={TextField}
                          type="text"
                          placeholder="У тебя 1 кубик"
                          validate={notEmpty}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field
                          name="genitiveSingular"
                          label="Род. падеж ед. число"
                          component={TextField}
                          type="text"
                          placeholder="У тебя 2 кубика"
                          validate={notEmpty}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field
                          name="genitivePlural"
                          label="Род. падеж мн. число"
                          component={TextField}
                          type="text"
                          placeholder="У тебя много кубиков"
                          validate={notEmpty}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field
                          name="accusativePlural"
                          label="Вин. падеж мн. число"
                          component={TextField}
                          type="text"
                          placeholder="Собирай кубики"
                          validate={notEmpty}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>

              <CardActions className={styles.actions}>
                <MutableButton
                  className={styles.accessButton}
                  loading={loading}
                  color="primary"
                  disabled={pristine}
                  size="medium"
                  type="submit"
                  variant="contained"
                >
                  Сохранить
                </MutableButton>
                <RedButton
                  color="primary"
                  size="medium"
                  type="button"
                  variant="contained"
                  onClick={onCancel}
                >
                  Отменить
                </RedButton>
              </CardActions>
            </Card>
          </form>
        );
      }}
    />
  );
};
